/**
 * Submit / approve / reject an announcement.
 *
 * Rejection requires a reason of at most 500 characters — the BFF enforces the limit and the
 * creator reads the reason on the card, so it is the only channel for that feedback.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';
import {
  approveWhatsNew,
  rejectWhatsNew,
  submitWhatsNewForApproval,
} from '../../Services/WhatsNew.service';
import { useActionToast } from '@/Hooks/useActionToast';

export type ApprovalAction = 'submit' | 'approve' | 'reject';

const MAX_REASON_LENGTH = 500;

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const messageStyle = css`
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const labelStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const textareaStyle = css`
  width: 100%;
  padding: var(--space-sm);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-focus);
  }
`;

const counterStyle = css`
  align-self: flex-end;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

const COPY: Record<ApprovalAction, { title: string; message: string; confirm: string }> = {
  submit: {
    title: 'Enviar para aprovação',
    message:
      'O anúncio sai de rascunho e vai para a fila de aprovação. Quem criou não pode aprovar o próprio item — outra pessoa precisa revisar.',
    confirm: 'Enviar',
  },
  approve: {
    title: 'Aprovar anúncio',
    message:
      'Aprovado, o anúncio passa a ser exibido para os usuários da plataforma do tenant na versão indicada.',
    confirm: 'Aprovar',
  },
  reject: {
    title: 'Rejeitar anúncio',
    message:
      'Explique o que precisa mudar. Quem criou lê este motivo no card e pode editar e reenviar.',
    confirm: 'Rejeitar',
  },
};

/** What the toast says once the action goes through. */
const SUCCESS_TITLE: Record<ApprovalAction, string> = {
  submit: 'Anúncio enviado para aprovação',
  approve: 'Anúncio aprovado',
  reject: 'Anúncio rejeitado',
};

const FAILURE_TITLE: Record<ApprovalAction, string> = {
  submit: 'Não foi possível enviar o anúncio',
  approve: 'Não foi possível aprovar o anúncio',
  reject: 'Não foi possível rejeitar o anúncio',
};

export interface ApprovalModalProps {
  open: boolean;
  action: ApprovalAction;
  whatsNewId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const ApprovalModal = ({
  open,
  action,
  whatsNewId,
  onClose,
  onSuccess,
}: ApprovalModalProps): JSX.Element => {
  const toast = useActionToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const copy = COPY[action];
  const trimmedReason = rejectionReason.trim();
  const isReasonMissing = action === 'reject' && trimmedReason.length === 0;

  const handleClose = () => {
    setRejectionReason('');
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (action === 'submit') {
        await submitWhatsNewForApproval(whatsNewId);
      } else if (action === 'approve') {
        await approveWhatsNew(whatsNewId);
      } else {
        await rejectWhatsNew(whatsNewId, trimmedReason);
      }

      toast.success(SUCCESS_TITLE[action]);
      setRejectionReason('');
      onSuccess();
      onClose();
    } catch (approvalError) {
      setError(
        approvalError instanceof Error
          ? approvalError.message
          : 'Não foi possível concluir a ação. Tente de novo.',
      );
      toast.failure(FAILURE_TITLE[action], approvalError);
    } finally {
      setIsLoading(false);
    }
  };

  const content = (
    <div className={bodyStyle}>
      <p className={messageStyle}>{copy.message}</p>

      {action === 'reject' ? (
        <div className={fieldStyle}>
          <label className={labelStyle} htmlFor='rejection-reason'>
            Motivo da rejeição
          </label>
          <textarea
            id='rejection-reason'
            className={textareaStyle}
            placeholder='Ex.: O texto do segundo item está maior que o espaço do modal.'
            value={rejectionReason}
            rows={4}
            maxLength={MAX_REASON_LENGTH}
            onChange={event => {
              setRejectionReason(event.target.value);
            }}
          />
          <span className={counterStyle}>
            {rejectionReason.length}/{MAX_REASON_LENGTH}
          </span>
        </div>
      ) : null}

      {error ? <span className={errorStyle}>{error}</span> : null}

      <div className={footerStyle}>
        <UTButton fitContent variant='tertiary' text='Cancelar' onClick={handleClose} />
        <UTButton
          fitContent
          id='approval-modal-confirm'
          text={isLoading ? 'Enviando...' : copy.confirm}
          disabled={isLoading || isReasonMissing}
          onClick={() => {
            void handleConfirm();
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={handleClose}
      title={copy.title}
      content={content}
      maxWidth='520px'
      hasButtonClose
      position='center'
    />
  );
};
